import { useMutation, useQuery, useQueryClient } from 'react-query'
import { api } from '../lib/api'

export interface PendingMigration {
  version: string
  filename: string
  name: string
}

export interface MigrationStatus {
  trackingAvailable: boolean
  isUpToDate: boolean
  pendingCount: number
  executedCount: number
  pendingMigrations: PendingMigration[]
  latestExecuted: string | null
  message: string | null
}

interface MigrationStatusResponse {
  success: boolean
  data: MigrationStatus
}

export interface MigrationRunResultItem {
  filename: string
  version: string
  skipped: boolean
  success: boolean
  message?: string
  error?: string
}

export interface MigrationRunResult {
  results: MigrationRunResultItem[]
  executedCount: number
  skippedCount: number
  failureCount: number
  stoppedAt: string | null
  success: boolean
}

interface MigrationRunResponse {
  success: boolean
  data: MigrationRunResult
  message?: string
}

export const MIGRATION_STATUS_QUERY_KEY = ['maintenance', 'migration-status'] as const

async function fetchMigrationStatus(): Promise<MigrationStatus | null> {
  const response = await api.get<MigrationStatusResponse>('/maintenance/migration-status')
  return response.data?.data ?? null
}

async function runMigrationsRequest(migrations: string | string[]): Promise<MigrationRunResponse> {
  const response = await api.post<MigrationRunResponse>('/maintenance/migrations/run', { migrations })
  return response.data
}

export function useMigrationStatus(options?: { enabled?: boolean; refetchInterval?: number | false }) {
  const enabled = options?.enabled ?? true
  const refetchInterval = options?.refetchInterval ?? 5 * 60_000

  return useQuery(MIGRATION_STATUS_QUERY_KEY, fetchMigrationStatus, {
    enabled,
    staleTime: 60_000,
    refetchInterval,
    retry: 1
  })
}

export function useRunMigrations() {
  const queryClient = useQueryClient()

  return useMutation(runMigrationsRequest, {
    onSuccess: () => {
      void queryClient.invalidateQueries(MIGRATION_STATUS_QUERY_KEY)
    }
  })
}
